/* src/contexts/ThemeContext.jsx */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

// Theme configurations
const themes = {
  light: {
    name: 'light',
    colors: {
      primary: '#2563EB',
      primaryHover: '#1D4ED8',
      primaryLight: '#93C5FD',
      secondary: '#F3F4F6',
      background: '#FFFFFF',
      backgroundSecondary: '#F9FAFB',
      surface: '#FFFFFF',
      surfaceElevated: '#F8FAFC',
      text: '#111827',
      textSecondary: '#6B7280',
      textTertiary: '#9CA3AF',
      border: '#E5E7EB',
      borderLight: '#F3F4F6',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    }
  },
  dark: {
    name: 'dark',
    colors: {
      primary: '#3B82F6',
      primaryHover: '#2563EB',
      primaryLight: '#60A5FA',
      secondary: '#1F2937',
      background: '#111827',
      backgroundSecondary: '#0F172A',
      surface: '#1F2937',
      surfaceElevated: '#374151',
      text: '#F9FAFB',
      textSecondary: '#D1D5DB',
      textTertiary: '#9CA3AF',
      border: '#374151',
      borderLight: '#4B5563',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
    }
  }
};

// Accent color options
const accentColors = {
  blue: '#3B82F6',
  purple: '#8B5CF6',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  pink: '#EC4899'
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Custom hook for responsive theme utilities
export const useResponsiveTheme = () => {
  const { theme } = useTheme();
  const [screenSize, setScreenSize] = useState('desktop');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width < 480) setScreenSize('mobile');
      else if (width < 768) setScreenSize('tablet');
      else if (width < 1024) setScreenSize('laptop');
      else setScreenSize('desktop');
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  return {
    ...theme,
    screenSize,
    isMobile: screenSize === 'mobile',
    isTablet: screenSize === 'tablet',
    isDesktop: screenSize === 'desktop' || screenSize === 'laptop'
  };
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem('theme-mode');
      if (stored) return JSON.parse(stored);
      
      // Check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
      return false;
    }
  });

  const [accentColor, setAccentColor] = useState(() => {
    try {
      const stored = localStorage.getItem('accent-color');
      return stored || 'blue';
    } catch {
      return 'blue';
    }
  });

  const [animations, setAnimations] = useState(() => {
    try {
      const stored = localStorage.getItem('animations-enabled');
      if (stored) return JSON.parse(stored);
      
      // Check system preference for reduced motion
      return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return true;
    }
  });

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually set a preference
      const hasManualPreference = localStorage.getItem('theme-mode');
      if (!hasManualPreference) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Listen to system motion preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e) => {
      setAnimations(!e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Persist theme preferences
  useEffect(() => {
    try {
      localStorage.setItem('theme-mode', JSON.stringify(isDark));
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
    }
  }, [isDark]);

  useEffect(() => {
    try {
      localStorage.setItem('accent-color', accentColor);
    } catch (error) {
      console.warn('Failed to save accent color preference:', error);
    }
  }, [accentColor]);

  useEffect(() => {
    try {
      localStorage.setItem('animations-enabled', JSON.stringify(animations));
    } catch (error) {
      console.warn('Failed to save animation preference:', error);
    }
  }, [animations]);

  // Update CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    const currentTheme = isDark ? themes.dark : themes.light;
    const currentAccent = accentColors[accentColor];

    // Set theme colors
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Set shadows
    Object.entries(currentTheme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Set accent color
    root.style.setProperty('--accent-color', currentAccent);

    // Set theme class on body
    document.body.className = isDark ? 'dark-theme' : 'light-theme';

    // Set animations preference
    root.style.setProperty('--animations-enabled', animations ? '1' : '0');
  }, [isDark, accentColor, animations]);

  // Theme context methods
  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  const setTheme = useCallback((themeName) => {
    setIsDark(themeName === 'dark');
  }, []);

  const changeAccentColor = useCallback((color) => {
    if (accentColors[color]) {
      setAccentColor(color);
    }
  }, []);

  const toggleAnimations = useCallback(() => {
    setAnimations(prev => !prev);
  }, []);

  // Computed theme object
  const theme = useMemo(() => ({
    isDark,
    current: isDark ? themes.dark : themes.light,
    accentColor,
    animations,
    colors: {
      ...(isDark ? themes.dark.colors : themes.light.colors),
      accent: accentColors[accentColor]
    },
    shadows: isDark ? themes.dark.shadows : themes.light.shadows
  }), [isDark, accentColor, animations]);

  // Context value
  const contextValue = useMemo(() => ({
    theme,
    isDark,
    accentColor,
    animations,
    availableAccentColors: Object.keys(accentColors),
    toggleTheme,
    setTheme,
    changeAccentColor,
    toggleAnimations,
    themes: Object.keys(themes),
    accentColors
  }), [theme, isDark, accentColor, animations, toggleTheme, setTheme, changeAccentColor, toggleAnimations]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Theme utility functions
export const getThemeColors = (isDark = false) => {
  return isDark ? themes.dark.colors : themes.light.colors;
};

export const getThemeShadows = (isDark = false) => {
  return isDark ? themes.dark.shadows : themes.light.shadows;
};

export const createThemeVariant = (baseTheme, overrides) => {
  return {
    ...baseTheme,
    colors: { ...baseTheme.colors, ...overrides.colors },
    shadows: { ...baseTheme.shadows, ...overrides.shadows }
  };
};

export default ThemeContext;